import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Scrivito from "scrivito";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useMutation } from "urql";
import NiceModal from "@ebay/nice-modal-react";
import cx from "classnames";

import { useTranslate } from "hooks";
import { ProgressBar } from "components/_Modals/ProjectDownloadModal/ProgressBar";
import { useSharedWorker } from "providers/SharedWorkerProvider/SharedWorkerContext";
import { pollingRequestStatuses } from "interfaces/enums";
import { useRequestsToLocalStorageManager } from "hooks/useRequestsToLocalStorageManager";

import { ReportsCheckBoxes } from "components/_Modals/ProjectDownloadModal/ReportsCheckboxes/ReportsCheckBoxes";
import { projectFolderReportConfig, wizardReportConfig } from "config/reports";
import {
  calculateInitialCounters,
  getSelectedReportIds,
} from "components/_Modals/ProjectDownloadModal/ReportsCheckboxes/helpers";
import { GenerateReportArchiveMut } from "api/mutations";
import { Button } from "../../Button";
import { Icon } from "../../Icon/Icon";
import s from "./ProjectDownloadModal.module.scss";
import { useLanguage } from "stores/globalStore";
import type { CheckboxAccordionItemProps } from "./ReportsCheckboxes/types";
import type { InitialOperation } from "interfaces/reports/sharedworker.types";
import { useProjectHasProducts } from "hooks/projectHooks";

type Props = {
  modalSubtitle: string;
  projectId: string;
  wizardId?: string;
  nodeId?: string;
};

type CheckboxCounters = {
  checkBoxesCount: number;
  checkBoxesSelected: number;
};

export const ProjectOrFolderDownloadModal = NiceModal.create(
  Scrivito.connect(({ modalSubtitle, projectId, wizardId, nodeId }: Props) => {
    const translate = useTranslate();
    const updateItemSelectionRef =
      useRef<
        NonNullable<
          CheckboxAccordionItemProps["updateItemSelectionRef"]
        >["current"]
      >(null);
    const modal = NiceModal.useModal();
    const lang = useLanguage();
    const { removeRequest } = useRequestsToLocalStorageManager();
    const initialData = useMemo(
      () => (wizardId ? wizardReportConfig : projectFolderReportConfig),
      [wizardId],
    );
    const [expandedAccordionIds, setExpandedAccordionIds] = useState<string[]>(
      [],
    );
    const { hasProducts } = useProjectHasProducts(projectId);

    const [{ checkBoxesCount, checkBoxesSelected }, setCheckboxSelectedCount] =
      useState<CheckboxCounters>(calculateInitialCounters(initialData));

    const [{ fetching: reportFetching }, requestReportArchive] = useMutation(
      GenerateReportArchiveMut,
    );

    const { startPolling, getMessageByNodeId, setActiveModal } =
      useSharedWorker();

    const requestInProgress = useMemo(
      () => getMessageByNodeId(wizardId || nodeId || projectId) || null,
      [getMessageByNodeId, wizardId, nodeId, projectId],
    );

    const handleChangeSelectedCount = useCallback(
      (countChange: number) => {
        setCheckboxSelectedCount((prev) => ({
          ...prev,
          checkBoxesSelected: prev.checkBoxesSelected + countChange,
        }));
      },
      [setCheckboxSelectedCount],
    );

    const handleSubmit = useCallback(() => {
      const reports = getSelectedReportIds(
        updateItemSelectionRef.current!.getCurrentItems(),
        translate,
      );

      requestReportArchive({
        input: {
          wizardNodeId: wizardId,
          projectId,
          languageCode: lang,
          reports,
          nodeId,
        },
      }).then((result) => {
        if (result?.data?.generateReportArchive?.requestId) {
          if (requestInProgress?.requestId) {
            removeRequest(requestInProgress.requestId);
          }
          startPolling(
            wizardId || projectId,
            result?.data?.generateReportArchive.requestId,
            {
              modalSubtitle,
              projectId,
              wizardId,
              initialOperation: result?.operation as InitialOperation,
            },
          );
        }
      });
    }, [
      lang,
      modalSubtitle,
      projectId,
      removeRequest,
      requestInProgress,
      requestReportArchive,
      startPolling,
      wizardId,
      nodeId,
      translate,
    ]);

    useEffect(() => {
      setActiveModal(modal.visible ? (wizardId ?? null) : null);
      return () => {
        setActiveModal(null);
      };
    }, [modal.visible, setActiveModal, wizardId]);

    const handleSelectAll = useCallback(() => {
      if (updateItemSelectionRef.current?.updateItems) {
        updateItemSelectionRef.current?.updateItems("all", true);
      }
      setExpandedAccordionIds(
        initialData.map((item) => `checkbox-on-${item.id}`),
      );
    }, [initialData]);

    const handleClearAll = useCallback(() => {
      if (updateItemSelectionRef.current?.updateItems) {
        updateItemSelectionRef.current?.updateItems("all", false);
      }
      setExpandedAccordionIds(
        initialData.map((item) => `checkbox-on-${item.id}`),
      );
    }, [initialData]);

    return (
      <Modal
        isOpen={modal.visible}
        centered={true}
        size="xl"
        toggle={modal.remove}
        scrollable={true}
      >
        <ModalHeader
          toggle={modal.remove}
          tag="div"
          className="d-flex align-items-start pb-0"
        >
          <h3 className="text-uppercase my-xxl-1 lh-32">
            {translate("DOWNLOAD_OPTIONS")}
          </h3>
          <h4 className="mb-0 lh-24">{modalSubtitle}</h4>
        </ModalHeader>
        <ModalBody
          className={cx(
            "d-flex row pb-8 pt-xs-6 gap-xs-5_5 position-relative",
            s.modalBodyScrollbar,
          )}
        >
          {!!requestInProgress && (
            <ProgressBar requestInProgress={requestInProgress} />
          )}
          <ReportsCheckBoxes
            blockTitle={translate("PROJECT_INFORMATION")}
            updateItemSelectionRef={updateItemSelectionRef}
            handleChangeSelectedCount={handleChangeSelectedCount}
            initialData={initialData}
            expandedAccordionIds={expandedAccordionIds}
            setExpandedAccordionIds={setExpandedAccordionIds}
            projectId={projectId}
          />
        </ModalBody>
        <ModalFooter className="pt-0">
          <Button
            data-testid="DownloadModal_select-all-btn"
            onClick={handleSelectAll}
            className="me-3"
            color="secondary"
            disabled={checkBoxesSelected === checkBoxesCount || !hasProducts}
            outline
          >
            {translate("SELECT_ALL")}
          </Button>
          <Button
            data-testid="DownloadModal_clear-selection-btn"
            onClick={handleClearAll}
            className="me-3"
            color="secondary"
            outline
            disabled={checkBoxesSelected === 0}
          >
            {translate("CLEAR_SELECTION")}
          </Button>
          <Button
            data-testid="DownloadModal_download-btn"
            className="d-inline-block text-white"
            onClick={handleSubmit}
            color="primary"
            isLoading={reportFetching}
            disabled={
              checkBoxesSelected === 0 ||
              requestInProgress?.status === pollingRequestStatuses.error
            }
          >
            <Icon iconName="arrow-down-bracket-regular" className="me-1" />
            {requestInProgress?.status === pollingRequestStatuses.error
              ? translate("RETRY_DOWNLOAD_DATA")
              : translate("DOWNLOAD_DATA")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }),
);
